import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import T from 'i18n-react';
import { DocumentSelectionModal } from '@adsk/bim360-matrix-react-components';
import { connect } from '../redux/connect';
import {
  fetchProjectFolders,
  fetchFolderContents,
  openPushpinSelection,
  closePushpinSelection,
} from '../redux/actions';
import { getEnv, getRegion } from '../redux/selectors';
import { filterPushpinSupportDocuments } from '../utils/attachmentsHelper';
import { ENV_CONFIGS } from '../constants/envs';
import {
  createGetFolderContentsMethod,
  createHasMoreFolderContents,
} from '../utils/docsApiHelpers';

class DocumentPicker extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isDocumentSelectionModalOpen: false,
      document: null,
    };
    this.handleMessage = this.handleMessage.bind(this);
    this.hideDocumentSelectionModal = this.hideDocumentSelectionModal.bind(this);
    this.handleLinkToDocumentSelection = this.handleLinkToDocumentSelection.bind(this);
    this.getProjectFolders = this.getProjectFolders.bind(this);
    this.getFolderContents = this.getFolderContents.bind(this);
    this.hasMoreFolderContents = this.hasMoreFolderContents.bind(this);
  }

  componentDidMount() {
    window.addEventListener('message', this.handleMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessage);
  }

  handleMessage(event) {
    const { env, region } = this.props;
    if (event.origin === ENV_CONFIGS[region][env].DOCS) {
      const { actionType } = event.data;
      // eslint-disable-next-line default-case
      switch (actionType) {
        case 'linkPushpinDone':
          this.handlePushpinSelection(event);
          break;
        case 'cancelLinkPushpin':
          this.handlePushpinCancel(event);
          break;
      }
    }
  }

  handlePushpinSelection(event) {
    const { pushpin, currentSheet } = event.data.params;
    this.props.closePushpinSelection(this.props.location);
    this.props.onSelectDocumentPushpin(this.state.document, pushpin, currentSheet);
  }

  handlePushpinCancel() {
    this.setState({ document: null });
    this.props.closePushpinSelection(this.props.location);
  }

  getProjectFolders() {
    const documentsCache = {};
    // This method is called every time we load the tree from scratch. That's why we need to clean the cache here - for it to be relevant for the current tree.
    this.getFolderContentsWrapped = createGetFolderContentsMethod(
      documentsCache,
      this.props.fetchFolderContents
    );
    this.hasMoreFolderContentsWrapped = createHasMoreFolderContents(documentsCache);
    return new Promise((resolve, reject) => {
      this.props.fetchProjectFolders(resolve, reject);
    });
  }

  getFolderContents(urn) {
    return this.getFolderContentsWrapped(urn).then((data) => filterPushpinSupportDocuments(data));
  }

  hasMoreFolderContents(context) {
    return this.hasMoreFolderContentsWrapped(context);
  }

  showDocumentSelectionModal() {
    this.setState({ isDocumentSelectionModalOpen: true });
  }

  hideDocumentSelectionModal() {
    this.setState({ isDocumentSelectionModalOpen: false });
  }

  handleLinkToDocumentSelection(urn, document) {
    this.setState({ document });
    this.hideDocumentSelectionModal();
    this.props.openPushpinSelection(document, this.props.status, this.props.location);
  }

  render() {
    const textSelect = T.translate('navigation.select');
    const classes = classNames('LinkToDocument', this.props.className);

    return (
      <div className={classes}>
        {this.props.children(() => this.showDocumentSelectionModal())}
        <DocumentSelectionModal
          container={this.props.container}
          isModalOpen={this.state.isDocumentSelectionModalOpen}
          closeModal={this.hideDocumentSelectionModal}
          isSaving={false}
          saveButtonText={textSelect}
          onChoosingDocument={this.handleLinkToDocumentSelection}
          getRootFolders={this.getProjectFolders}
          getFolderContents={this.getFolderContents}
          hasMoreFolderContents={this.hasMoreFolderContents}
          documentSelectionTreeProps={{ style: { padding: '20px 30px' } }}
        />
      </div>
    );
  }
}

DocumentPicker.displayName = 'LinkToDocument';

DocumentPicker.propTypes = {
  children: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  env: PropTypes.string.isRequired,
  container: PropTypes.object.isRequired,
  onSelectDocumentPushpin: PropTypes.func.isRequired,
  fetchProjectFolders: PropTypes.func.isRequired,
  fetchFolderContents: PropTypes.func.isRequired,
  openPushpinSelection: PropTypes.func.isRequired,
  closePushpinSelection: PropTypes.func.isRequired,
  className: PropTypes.string,
  location: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    region: getRegion(state),
    env: getEnv(state),
  };
};

export default connect(mapStateToProps, {
  fetchProjectFolders,
  fetchFolderContents,
  openPushpinSelection,
  closePushpinSelection,
})(withRouter(DocumentPicker));
